import { useCallback, useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  TERMS_INTRO,
  TERMS_LAST_UPDATED,
  TERMS_SECTIONS,
} from '@/content/terms-of-service';

const SCROLL_THRESHOLD_PX = 8;

interface Props {
  accepted: boolean;
  onAcceptChange: (accepted: boolean) => void;
}

function isScrolledToBottom(el: HTMLDivElement): boolean {
  const noScrollNeeded = el.scrollHeight <= el.clientHeight + SCROLL_THRESHOLD_PX;
  const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD_PX;
  return noScrollNeeded || atBottom;
}

export function TermsOfServicePanel({ accepted, onAcceptChange }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [scrollBlockedMessage, setScrollBlockedMessage] = useState<string | null>(null);

  const updateScrollPosition = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setHasReachedBottom(isScrolledToBottom(el));
  }, []);

  useEffect(() => {
    updateScrollPosition();
    const el = scrollRef.current;
    if (!el) return;

    const observer = new ResizeObserver(() => updateScrollPosition());
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollPosition]);

  const handleScroll = () => {
    updateScrollPosition();
    if (scrollBlockedMessage) setScrollBlockedMessage(null);
  };

  const handleAcceptClick = () => {
    if (accepted) {
      onAcceptChange(false);
      setScrollBlockedMessage(null);
      return;
    }
    if (!hasReachedBottom) {
      setScrollBlockedMessage('Please scroll through the full Terms first');
      return;
    }
    onAcceptChange(true);
    setScrollBlockedMessage(null);
  };

  const canAccept = hasReachedBottom || accepted;

  return (
    <div className="space-y-3">
      <div className="text-[11px] font-bold uppercase tracking-widest text-gold-deep">
        Terms of Service
      </div>
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="max-h-[220px] overflow-y-auto rounded-[10px] border border-line bg-paper px-3.5 py-3 text-[11.5px] leading-relaxed text-slate-600"
        >
          <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Last updated: {TERMS_LAST_UPDATED}
          </p>
          {TERMS_INTRO.map((p) => (
            <p key={p.slice(0, 32)} className="mb-3">{p}</p>
          ))}
          {TERMS_SECTIONS.map((section) => (
            <div key={section.title} className="mb-3">
              <p className={`mb-1.5 font-semibold text-navy ${section.emphasis ? 'text-[10.5px] uppercase tracking-wide' : ''}`}>
                {section.title}
              </p>
              {section.paragraphs.map((p) => (
                <p
                  key={p.slice(0, 40)}
                  className={`mb-2 last:mb-0 ${section.emphasis ? 'text-[10.5px] uppercase tracking-wide text-slate-500' : ''}`}
                >
                  {p}
                </p>
              ))}
            </div>
          ))}
        </div>
        {!hasReachedBottom && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-14 rounded-b-[10px] bg-gradient-to-t from-paper via-paper/90 to-transparent"
            aria-hidden
          />
        )}
      </div>
      {!hasReachedBottom && (
        <p className="text-center text-[10.5px] text-slate-400">
          Scroll to the bottom to continue
        </p>
      )}
      {scrollBlockedMessage && (
        <p className="text-center text-[10.5px] font-medium text-amber-700" role="alert">
          {scrollBlockedMessage}
        </p>
      )}
      <button
        type="button"
        onClick={handleAcceptClick}
        className={`flex w-full items-center gap-2.5 rounded-[10px] border-[1.5px] p-3 text-left transition-colors ${
          accepted
            ? 'border-green-700 bg-green-50/80'
            : canAccept
              ? 'border-line bg-paper'
              : 'cursor-pointer border-line bg-paper opacity-70'
        }`}
      >
        <div
          className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-[1.5px] ${
            accepted ? 'border-green-700 bg-green-700' : 'border-line bg-paper'
          }`}
        >
          {accepted && <Check size={14} className="text-cream" strokeWidth={3} />}
        </div>
        <span className="text-xs font-semibold leading-snug text-ink">
          I&apos;ve read and agree to the Terms of Service, and I understand that Bag Tag is not affiliated with Army Navy Country Club.
        </span>
      </button>
    </div>
  );
}
